import type { SupportCenterFormKind } from './supportCenterSpecialties'
import type { SupportCenterElegibilidadeEspecialidade } from './supportCenterAgenda'
import type {
  SupportCenterAppointment,
  SupportCenterAppointmentStatus,
  SupportCenterTriageStatus,
} from './supportCenterTriage'

export type { SupportCenterAppointment, SupportCenterAppointmentStatus, SupportCenterFormKind, SupportCenterTriageStatus }

export type SupportCenterAppointmentContactOwner = 'holder' | 'patient'

/** Canais gravados em support_center_appointments.channel. */
export type SupportCenterAppointmentChannel =
  | 'whatsapp'
  | 'telefone'
  | 'videochamada'
  | 'whatsapp-bot'

/** Canais selecionáveis no formulário da Central Amparo (sem bot). */
export type SupportCenterAppointmentFormChannel = 'whatsapp' | 'telefone' | 'videochamada'

/** Canais selecionáveis no cancelamento (POST /agendamentos/{id}/cancelar). */
export type SupportCenterAppointmentCancelChannel = 'whatsapp' | 'telefone' | 'videochamada'

export const SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS: Record<
  SupportCenterAppointmentChannel,
  string
> = {
  whatsapp: 'WhatsApp',
  telefone: 'Telefone',
  videochamada: 'Videochamada',
  'whatsapp-bot': 'WhatsApp Bot',
}

export const SUPPORT_CENTER_APPOINTMENT_FORM_CHANNEL_OPTIONS: Array<{
  value: SupportCenterAppointmentFormChannel
  label: string
}> = [
  { value: 'telefone', label: 'Telefone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'videochamada', label: 'Videochamada' },
]

export const SUPPORT_CENTER_APPOINTMENT_CANCEL_CHANNEL_OPTIONS: Array<{
  value: SupportCenterAppointmentCancelChannel
  label: string
}> = [
  { value: 'telefone', label: 'Telefone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'videochamada', label: 'Videochamada' },
]

export const SUPPORT_CENTER_APPOINTMENT_CHANNEL_FILTER_OPTIONS: Array<{
  value: SupportCenterAppointmentChannel
  label: string
}> = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telefone', label: 'Telefone' },
  { value: 'videochamada', label: 'Videochamada' },
  { value: 'whatsapp-bot', label: 'WhatsApp Bot' },
]

export function appointmentChannelLabel(
  channel: string | null | undefined
): string {
  if (!channel?.trim()) return '—'
  const key = channel.trim() as SupportCenterAppointmentChannel
  return SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS[key] || key
}

export type SupportCenterAppointmentCreateInput = {
  holder_cpf: string
  holder_name: string
  holder_birth_date?: string | null
  patient_cpf: string
  patient_name: string
  patient_birth_date?: string | null
  patient_is_insured: boolean
  relationship_to_holder?: string | null
  internal_emails: string[]
  /**
   * E-mails do paciente/segurado (ordem do formulário).
   * O POST /agendamentos envia só o primeiro em email_paciente (string);
   * os demais são gravados em people_contacts + email_contact_ids após o create.
   */
  patient_emails: string[]
  /** Telefone do formulário — enviado à API em telefone_paciente (a API grava o contato). */
  patient_phone?: string | null
  /**
   * Pessoa de quem o formulário copiou o contato.
   * O POST não usa este campo: e-mail e telefone saem sempre como do paciente.
   */
  contact_owner?: SupportCenterAppointmentContactOwner
  /** Canal de origem do agendamento (telefone, whatsapp ou videochamada neste sistema). */
  channel: SupportCenterAppointmentFormChannel
  specialization_id: string
  /** Código da assistência escolhida no modal — enviado como codigo_assistencia. */
  assistance_code: string
  /** Preferencial: e-mail do profissional (contrato IA-API). */
  professional_email?: string
  /** Fallback: resolve e-mail via RPC antes do POST. */
  professional_user_id?: string
  scheduled_at: string
  /**
   * Especialidades + coberturas da GET /elegibilidade (apólice, convênio, fornecedor, corretor, cliente).
   * Obrigatório para montar cod_apolice / cod_convenio / cod_fornecedor_assistencia / cod_corretor / cod_cliente
   * quando o titular está Ativo. Em Falecido, use holder_deceased e códigos 0.
   */
  elegibilidadeEspecialidades?: SupportCenterElegibilidadeEspecialidade[]
  /** Titular falecido: não consulta elegibilidade; POST envia cod_* = 0. */
  holder_deceased?: boolean
  /**
   * Id do pré-agendamento a promover no POST /agendamentos (`id_pre_agendamento`).
   * Sem este campo a API cria um agendamento novo.
   */
  pre_booking_id?: string
}

/** Payload do pré-agendamento (passos 1, 2 e 4) → POST /agendamentos/pre. */
export type SupportCenterPreBookingInput = {
  holder_cpf: string
  holder_name?: string | null
  holder_birth_date?: string | null
  /** Titular falecido: API usa titular_situacao=falecido (sem PASI; códigos 0). */
  holder_deceased?: boolean
  patient_cpf: string
  patient_name?: string | null
  patient_birth_date?: string | null
  relationship_to_holder?: string | null
  specialization_id: string
  assistance_code: string
  professional_user_id: string
  professional_email?: string
  scheduled_at: string
  channel: SupportCenterAppointmentFormChannel
  internal_emails?: string[]
}

export type SupportCenterAppointmentUpdateInput = SupportCenterAppointmentCreateInput & {
  id: string
}

export type SupportCenterAppointmentListFilters = {
  range_start: string
  range_end: string
  /** Filtro pelo profissional responsável (professional_user_id). */
  professional_user_id?: string
  /** @deprecated Preferir professional_user_id. Escopo legado: contains(internal_emails, [email]). */
  attendant_email?: string
  specialization_id?: string
  statuses?: SupportCenterAppointmentStatus[]
  /** Inclui cancelados (status=cancelado ou soft-delete incompleto marcado/reagendado). */
  include_cancelled?: boolean
}

export type SupportCenterAttendantOption = {
  id: string
  name: string
  email: string
}

export type SupportCenterAgendaEvent = SupportCenterAppointment & {
  professional_user_id?: string | null
  confirmation_requested_at?: string | null
  /** Status da triagem vinculada (quando existir). */
  triage_status?: SupportCenterTriageStatus | null
  /** Data da triagem (YYYY-MM-DD) quando a triagem vinculada existir. */
  triage_date?: string | null
  /**
   * true = formulário de atendimento já enviado (submitted_at).
   * false/undefined = ainda não enviado (ou não carregado).
   */
  attendance_form_submitted?: boolean | null
}

export type SupportCenterHolderVitalStatus = 'ativo' | 'falecido'

export const EMPTY_APPOINTMENT_FORM = {
  holder_cpf: '',
  /** Situação do titular no formulário (não persiste no banco nesta entrega). */
  holder_status: 'ativo' as SupportCenterHolderVitalStatus,
  holder_name: '',
  holder_birth_date: '',
  patient_cpf: '',
  patient_name: '',
  patient_birth_date: '',
  patient_is_insured: true,
  relationship_to_holder: '',
  internal_emails: [] as string[],
  client_emails: [] as string[],
  patient_phone: '',
  /** Origem do pré-preenchimento. O POST envia o 1º de client_emails em email_paciente. */
  contact_owner: 'holder' as SupportCenterAppointmentContactOwner,
  /** Código da assistência escolhida no passo 2 (elegibilidade). */
  assistance_code: '',
  /** Canal de agendamento (telefone | whatsapp | videochamada). Vazio até o usuário escolher. */
  channel: '' as '' | SupportCenterAppointmentFormChannel,
  specialization_id: '',
  professional_user_id: '',
  scheduled_at: '',
}

export type SupportCenterAppointmentFormState = typeof EMPTY_APPOINTMENT_FORM

/** Anexo gravado no início do atendimento (bucket support-center-appointments). */
export type SupportCenterAppointmentAttachment = {
  id: string
  appointment_id: string
  file_path: string
  file_name: string
  file_size: number | null
  mime_type: string | null
  uploaded_by: string | null
  created_at: string
  deleted_at: string | null
}

export const SUPPORT_CENTER_ATTACHMENT_BUCKET = 'support-center-appointments'
export const SUPPORT_CENTER_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024 // 10 MB
export const SUPPORT_CENTER_ATTACHMENT_MAX_MB = 10

export const SUPPORT_CENTER_ATTACHMENT_ACCEPT =
  '.doc,.docx,.pdf,.jpg,.jpeg,.png,.gif,.webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/jpeg,image/png,image/gif,image/webp'

export const SUPPORT_CENTER_ATTACHMENT_EXTENSIONS = [
  '.doc',
  '.docx',
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
] as const

export const SUPPORT_CENTER_ATTACHMENT_MIME_BY_EXT: Record<string, string> = {
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.pdf': 'application/pdf',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
}
